import { createRouter, createWebHistory } from 'vue-router';

import { toBase32 } from '@/utils/toBase32.js';
import store from '@/store';
import { saveScrollPosition, restoreScrollPosition } from '@/utils/scrollControl';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/index.vue'),
    },
    {
      path: '/sign-in-with-x',
      name: 'sign-in-with-x',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/sign-in-with-x.vue'),
    },
    {
      path: '/oauth-callback-bluesky',
      name: 'oauth-callback-bluesky',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/oauth-callback-bluesky.vue'),
    },
    {
      path: '/declaration',
      name: 'declarationPage',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/declarationPage.vue'),
    },
    {
      path: '/about',
      name: 'aboutPage',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/aboutPage.vue'),
    },
    {
      path: '/privacy',
      name: 'privacy',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/privacy.vue'),
    },
    {
      path: '/term-of-service',
      name: 'terms',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/termsOfService.vue'),
    },
    {
      path: '/post/:id',
      name: 'post',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/post.vue'),
    },
    {
      path: '/reply/:id',
      name: 'reply',
      meta: { layout: 'default', auth: true, isReply: true },
      component: () => import('../views/post.vue'),
    },
    {
      path: '/sign-in',
      name: 'sign-in',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/sign-in.vue'),
    },
    {
      path: '/sign-up',
      name: 'sign-up',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/sign-up.vue'),
    },
    {
      path: '/profile/:id',
      name: 'profile',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/profile.vue'),
    },
    {
      path: '/org/:id',
      name: 'org',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/org.vue'),
    },
    {
      path: '/channel/:id',
      name: 'channel',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/channel.vue'),
    },
    {
      path: '/user/:username',
      name: 'username',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/profile.vue'),
    },
    {
      path: '/profile/:id',
      name: 'profile',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/profile.vue'),
    },
    {
      path: '/search',
      name: 'search',
      meta: { layout: 'default', auth: true },
      component: () => import('../components/Rightbar/index.vue'),
    },
    {
      path: '/onboarding/create-account-x',
      name: 'onboarding-create-account-x',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/CreateAccountX.vue'),
    },
    {
      path: '/onboarding/verification-code-x',
      name: 'onboarding-verification-code-x',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/VerificationCodeX.vue'),
    },
    {
      path: '/onboarding/login-method',
      name: 'onboarding-login-method',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/LoginMethod.vue'),
    },
    {
      path: '/onboarding/create-account',
      name: 'onboarding-create-account',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/CreateAccount.vue'),
    },
    {
      path: '/onboarding/verification-code',
      name: 'onboarding-verification-code',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/VerificationCode.vue'),
    },
    {
      path: '/onboarding/create-password',
      name: 'onboarding-create-password',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/CreatePassword.vue'),
    },
    {
      path: '/onboarding/create-username',
      name: 'onboarding-create-username',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/CreateUsername.vue'),
    },
    {
      path: '/onboarding/add-avatar',
      name: 'onboarding-add-avatar',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/AddAvatar.vue'),
    },
    {
      path: '/connections/:type/:id',
      name: 'connections',
      meta: { layout: 'default' },
      component: () => import('../views/followers.vue'),
    },
    // {
    //   path: "/following/:id",
    //   name: "FollowingList",
    //   component: FollowingList,
    //   meta: { layout: "default", auth: true },
    // },
    {
      path: '/notifications',
      name: 'notifications',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/notifications.vue'),
    },
    {
      path: '/orgs-groups',
      name: 'orgs-groups',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/orgs-groups.vue'),
    },
    {
      path: '/l/:id',
      name: 'check-visit-links',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/check-visit-links.vue'),
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/verify-email.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      meta: { layout: 'guest' },
      component: () => import('../components/Page404/index.vue'),
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // If this is a transition from a post back to the home page, don't reset the scroll
    if (from.name === 'post' && to.name === 'home') {
      return false; // Prevent automatic scrolling
    }

    // For all other cases, use standard behavior
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  if (to.path === '/') {
    return next();
  }

  const timestampQuery = Object.keys(to.query)[0];
  if (!timestampQuery) {
    const timestamp = Date.now();
    const base32Value = toBase32(timestamp);
    next({ ...to, query: { [base32Value]: '' } });
  } else {
    next();
  }
});

let previousRoute = null;
router.beforeEach((to, from, next) => {
  // Save scroll position when leaving the home page or profile
  if (from.name === 'home' || from.name === 'profile' || from.name === 'username') {
    saveScrollPosition(from.name);
  }

  next();
  previousRoute = from;
});

router.afterEach((to, from) => {
  // Restore scroll position when returning to the home page or profile
  if (
    (to.name === 'home' && from.name === 'post') ||
    to.name === 'username' ||
    (to.name === 'profile' && (from.name === 'post' || from.name === 'reply'))
  ) {
    // Small delay for the component to fully load
    restoreScrollPosition(to.name, 300);
  }

  // Only send tracking if the path actually changed
  if (to.path !== from.path) {
    store.dispatch('sendTrackPage', { url: to.path });
  }
});

export function goBack() {
  const currentRoute = router.currentRoute.value;
  if (currentRoute.name === 'home') return;
  if (router.options.history.state.back) {
    router.go(-1);
  } else {
    router.push({ name: 'home' });
  }
}

export { previousRoute };
export default router;
